<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'ChallengeManagement'})"/>
                {{ $t('health.challenge.history') }}
              </h1>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row column-align="center">
          <grid-col col-size="4">
            <h2>{{ $t('health.challenge.finishedChallenges') }}</h2>
          </grid-col>
        </grid-row>
        <grid-row class="margin-bottom-xxl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="challengesTable"
              :data="fetchChallenges"
              :show-caption="false"
              class="datatable"
              :show-filter="true">
              <table-column show="id" label="Id" :hidden="true"></table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="displayChallengeModal(row)"
                    :icon="icon.info"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                show="name"
                :label="$t('health.challenge.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="nameEng"
                :label="$t('health.challenge.nameEng')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="type"
                :label="$t('health.challenge.type')"
                :formatter="formatType"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="category"
                :formatter="formatCategory"
                :label="$t('health.challenge.category')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="startDate"
                :label="$t('health.challenge.startDate')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="endDate"
                :label="$t('health.challenge.endDate')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                show="participants"
                :label="$t('health.challenge.participants')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="displayChallengeParticipantsModal(row)"
                    :icon="icon.user"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
      <challenge-data-modal
        v-if="showChallengeModal"
        :challenge="selectedChallenge"
        v-on:close="closeChallengeModal"
      />
      <challenge-participants-modal
        v-if="showChallengeParticipantsModal"
        :challenge="selectedChallenge"
        v-on:close="closeChallengeParticipantsModal"
      />
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout.vue';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableRow from '@/components/shared/table/components/TableRow';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import challengeApi from 'BRAND/api/challenge';
import Alert from '@/components/shared/alert/Alert';
import ChallengeDataModal from 'BRAND/components/challenge/ChallengeDataModal';
import LaunchChallengeModal from 'BRAND/components/challenge/LaunchChallengeModal';
import ChallengeParticipantsModal from 'BRAND/components/challenge/ChallengeParticipantsModal';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faInfoCircle, faUser
} from '@fortawesome/pro-regular-svg-icons';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';

export default {
  name: 'ChallengeHistory',
  components: {
    MainLayout,
    GridCol,
    GridRow,
    Chevron,
    TableComponent,
    TableRow,
    TableColumn,
    Alert,
    ChallengeDataModal,
    LaunchChallengeModal,
    FontAwesomeIcon,
    OkCancelModal,
    ChallengeParticipantsModal
  },
  data () {
    return {
      challenges: [],
      showChallengeModal: false,
      showChallengeParticipantsModal: false,
      selectedChallenge: null,
      icon: {
        info: faInfoCircle,
        user: faUser
      },
      /* Table */
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      /* Alerts */
      alertVisible: false,
      alertMessage: '',
      alertType: ''
    };
  },
  created () {
    this.requestData();
  },
  watch: {
    getChallenges: {
      handler: function () {
        this.$refs.challengesTable.refresh();
      }
    }
  },
  computed: {
    getBreadcrumb () {
      const self = this.$t('health.challenge.history');
      return [
        {name: this.$t('menu.workspace'), link: '/workspace'},
        {name: this.$t('health.menu.challengeManagement'), link: '/challenge-management'},
        {name: self, link: '/challenge/history', disabled: true}
      ];
    },
    getChallenges () {
      return this.challenges;
    }
  },
  methods: {
    requestData () {
      challengeApi.getPastChallenges(this).then(data => {
        this.challenges = data;
      }).catch(() => {
        // TODO (Display error)
      });
    },
    fetchChallenges ({page, filter, sort}) {
      page = page === null || isNaN(page) ? 1 : page;
      const tablePage = pagination.createPage(
        this.getChallenges,
        this.tableState,
        ['name', 'nameEng'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    displayChallengeModal (challenge) {
      this.selectedChallenge = challenge;
      this.showChallengeModal = true;
    },
    displayChallengeParticipantsModal (challenge) {
      this.selectedChallenge = challenge;
      this.showChallengeParticipantsModal = true;
    },
    closeChallengeModal () {
      this.showChallengeModal = false;
      this.selectedChallenge = null;
    },
    closeChallengeParticipantsModal () {
      this.showChallengeParticipantsModal = false;
      this.selectedChallenge = null;
    },
    formatType (value) {
      return this.$t('health.challenge.types.' + value);
    },
    formatCategory (value) {
      return value != null ? this.$t('health.challenge.categories.' + value) : '';
    }
  }
};
</script>

<style lang="scss">
@import "src/stylesheets/variables";

</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/challenge/ChallengeHistory.vue